<template>
  <div class="handbook-balance">
    <oxd-text tag="h6" class="orangehrm-main-title">
      {{ separation ? 'Vacation separation review' : 'My handbook balances' }}
    </oxd-text>
    <oxd-text v-if="summary.asOf" class="balance-description">
      {{ summary.year }} leave year · Earned and taken through
      {{ summary.asOf }}. Amounts are in days unless hours are shown.
    </oxd-text>
    <oxd-text
      v-for="(warning, index) in summary.warnings || []"
      :key="index"
      class="balance-warning"
      role="status"
    >
      {{ warning }}
    </oxd-text>
    <template v-if="vacation.posted">
      <oxd-text type="card-title">Vacation</oxd-text>
      <oxd-grid :cols="3" class="orangehrm-full-width-grid">
        <oxd-grid-item v-for="metric in vacationMetrics" :key="metric.label">
          <div class="balance-metric">
            <oxd-text>{{ metric.label }}</oxd-text>
            <oxd-text type="card-title">{{ metric.value }}</oxd-text>
          </div>
        </oxd-grid-item>
      </oxd-grid>
      <oxd-text v-if="!separation" class="balance-description">
        Available days reflect the posted handbook bank after reservations.
        Vacation used in advance includes leave marked taken, after earned
        vacation and carryover are used. Pending and future leave are excluded
        from that figure.
      </oxd-text>
      <oxd-text
        v-if="summary.vacationCarryoverProtected"
        class="balance-description"
      >
        Unused earned vacation is protected for carryover for this employee.
      </oxd-text>
    </template>
    <template v-if="!separation && sick.posted">
      <oxd-text type="card-title">Sick and Safe Leave</oxd-text>
      <oxd-grid :cols="3" class="orangehrm-full-width-grid">
        <oxd-grid-item v-for="metric in sickMetrics" :key="metric.label">
          <div class="balance-metric">
            <oxd-text>{{ metric.label }}</oxd-text>
            <oxd-text type="card-title">{{ metric.value }}</oxd-text>
          </div>
        </oxd-grid-item>
      </oxd-grid>
    </template>
    <oxd-text class="balance-description">
      Sick and Safe Leave is not subject to repayment. Unused sick leave carries
      forward<span v-if="summary.sickBalanceCapHours != null">
        subject to the {{ summary.sickBalanceCapHours }}-hour balance cap</span
      >.
      <span v-if="summary.sickBalanceCapHours != null">
        New annual grants are limited to the available room in the sick leave
        bank. Using sick leave does not trigger another annual grant.
      </span>
    </oxd-text>
    <oxd-text v-if="separation" class="balance-description">
      This review counts only leave marked taken on or before the selected date.
      Pending, future and unconfirmed approved leave are excluded. Potential
      repayment hours require HR/payroll review of records, adjustments and
      applicable requirements. This review does not establish a debt, calculate
      money, authorize a deduction, or change leave or payroll records.
    </oxd-text>
  </div>
</template>

<script>
export default {
  name: 'HandbookBalance',
  props: {
    summary: {type: Object, required: true},
    separation: {type: Boolean, default: false},
  },
  computed: {
    vacation() {
      return this.summary.benefits?.vacation || {};
    },
    sick() {
      return this.summary.benefits?.sick || {};
    },
    vacationMetrics() {
      const fields = this.separation
        ? [
            ['Earned this year', 'earnedDays'],
            ['Earned carryover', 'carryoverDays'],
            ['Actual vacation taken', 'takenDays'],
            ['Earned unused vacation (hours)', 'earnedUnusedHours'],
            ['Potential repayment review (hours)', 'potentialRepaymentHours'],
            ['Manual adjustment needing review (days)', 'adjustmentDays'],
          ]
        : [
            [
              this.summary.vacationFrontLoaded
                ? 'Annual vacation granted'
                : 'Vacation credited',
              'grantedDays',
            ],
            ['Available in handbook bank', 'availableDays'],
            ['Earned this year', 'earnedDays'],
            ['Vacation carryover', 'carryoverDays'],
            ['Vacation used in advance', 'usedInAdvanceDays'],
            ['Pending / approved / future leave', 'reservedDays'],
          ];
      return fields.map(([label, key]) => ({
        label,
        value: this.days(this.vacation[key]),
      }));
    },
    sickMetrics() {
      return [
        ['New sick leave granted', 'grantedDays'],
        ['Available in handbook bank', 'availableDays'],
        ['Sick leave carryover', 'carryoverDays'],
      ].map(([label, key]) => ({label, value: this.days(this.sick[key])}));
    },
  },
  methods: {
    days(value) {
      return value == null ? 'Unavailable' : Number(value).toFixed(4);
    },
  },
};
</script>

<style lang="scss" scoped>
.balance-description {
  margin: 0.75rem 0;
  line-height: 1.6;
}
.balance-warning {
  padding: 0.75rem;
  margin: 0.5rem 0;
  background: $oxd-background-pastel-white-color;
}
.balance-metric {
  padding: 0.75rem 0;
}
</style>
